/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import HelpIcon from '@mui/icons-material/Help';
import {
  AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  OutlinedInput,
  Tooltip,
} from '@mui/material';

import { HUAutocomplete } from '../../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../../elements/HUTextInput/utils';
import { OutlinedPasswordInput } from '../../../../../elements/OutlinedPasswordInput/OutlinedPasswordInput';
import { Namespaces } from '../../../../../i18n';
import { useFormStyles } from '../../../../../styles/form';
import { ObjectMap } from '../../../../../types/utils/ObjectMap';
import { ApiValidationGetFieldError, ApiValidationHasFieldError } from '../../../../../utils/useApiValidation';
import { useValidationTranslation } from '../../../../../utils/useValidationTranslation';
import { EmailType } from '../../../../SystemConfigurationPage/SystemConfigurationPage.consts';
import { SystemConfigurationFormValues } from '../../../../SystemConfigurationPage/SystemConfigurationPage.types';
import { useSetupPageStyles } from '../../../SetupPage.styles';

type EmailConfigurationFormProps = FormikProps<SystemConfigurationFormValues> & {
  isFetching: boolean;
  hasFieldApiError: ApiValidationHasFieldError<ObjectMap>;
  getFieldApiError: ApiValidationGetFieldError<ObjectMap>;
  onSkip: () => void;
};

export const EmailConfigurationForm = memo<EmailConfigurationFormProps>(props => {
  const {
    touched,
    values,
    errors,
    handleChange,
    handleBlur,
    hasFieldApiError,
    getFieldApiError,
    setFieldValue,
    handleSubmit,
    isFetching,
    onSkip,
  } = props;
  const { t, i18n } = useTranslation(Namespaces.Admin);
  const { vtr } = useValidationTranslation();
  const classes = useSetupPageStyles();
  const formClasses = useFormStyles();

  const shouldUseGmail = !!values.emailAddress && !!values.emailToken;

  const googleHelpUrl = useMemo(
    () => `https://support.google.com/accounts/answer/185833?hl=${encodeURIComponent(i18n.language)}`,
    [i18n.language],
  );

  useEffect(() => {
    void setFieldValue('useSmtp', !shouldUseGmail);
  }, [setFieldValue, shouldUseGmail]);

  useEffect(() => {
    void setFieldValue('type', shouldUseGmail ? EmailType.GMAIL : EmailType.SMTP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasError = useCallback(
    (name: keyof SystemConfigurationFormValues): boolean => {
      return !!touched[name] && (!!errors[name] || hasFieldApiError(name));
    },
    [errors, hasFieldApiError, touched],
  );

  const getError = useCallback(
    (name: keyof SystemConfigurationFormValues): string => {
      const error = errors[name] || getFieldApiError(name);
      return error ? error : '';
    },
    [errors, getFieldApiError],
  );

  const renderAutocompleteInput = useCallback(
    (renderParams: AutocompleteRenderInputParams) => (
      <>
        <InputLabel htmlFor="type">{t('email_configuration.form.type')}</InputLabel>
        <OutlinedInput
          {...destructRenderInputParams(renderParams)}
          onBlur={handleBlur}
          name="type"
          label={t('email_configuration.form.type')}
          placeholder={t('email_configuration.form.type')}
          disabled={isFetching}
          readOnly
        />
      </>
    ),
    [handleBlur, isFetching, t],
  );

  const handleChangeForm = useCallback(
    async (_e: unknown, type: EmailType) => {
      await setFieldValue('type', type);

      await Promise.all([
        setFieldValue('smtpHost', ''),
        setFieldValue('smtpPort', ''),
        setFieldValue('smtpSecure', false),
        setFieldValue('smtpUser', ''),
        setFieldValue('smtpPass', ''),
        setFieldValue('smtpFromEmail', ''),
        setFieldValue('emailAddress', ''),
        setFieldValue('emailToken', ''),
      ]);
    },
    [setFieldValue],
  );

  const getPasswordVisibilityLabel = useCallback(
    (name: 'smtp' | 'gmail', type: 'show' | 'hide') =>
      t(`email_configuration.${name === 'smtp' ? 'password' : 'token'}.${type}`),
    [t],
  );

  const smtpForm = useMemo(
    () => (
      <>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('smtpHost')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpHost">{t('email_configuration.form.hostname')}</InputLabel>
          <OutlinedInput
            name="smtpHost"
            label={t('email_configuration.form.hostname')}
            value={values.smtpHost}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            data-cy="create-email-configuration-form-smtp-host-input"
          />
          {hasError('smtpHost') && (
            <FormHelperText data-cy="create-email-configuration-form-smtp-host-error">
              {vtr(getError('smtpHost'), {
                field: t('email_configuration.form.hostname'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('smtpPort')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpPort">{t('email_configuration.form.port')}</InputLabel>
          <OutlinedInput
            name="smtpPort"
            label={t('email_configuration.form.port')}
            value={values.smtpPort}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            data-cy="create-email-configuration-form-smtp-port-input"
          />
          {hasError('smtpPort') && (
            <FormHelperText data-cy="create-email-configuration-form-smtp-port-error">
              {vtr(getError('smtpPort'), {
                field: t('email_configuration.form.port'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('smtpUser')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpUser">{t('email_configuration.form.username')}</InputLabel>
          <OutlinedInput
            name="smtpUser"
            label={t('email_configuration.form.username')}
            value={values.smtpUser}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            data-cy="create-email-configuration-form-username-input"
          />
          {hasError('smtpUser') && (
            <FormHelperText data-cy="create-email-configuration-form-username-error">
              {vtr(getError('smtpUser'), {
                field: t('email_configuration.form.username'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('smtpPass')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpPass">{t('email_configuration.form.password')}</InputLabel>
          <OutlinedPasswordInput
            name="smtpPass"
            label={t('email_configuration.form.password')}
            value={values.smtpPass}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            showTooltip={getPasswordVisibilityLabel('smtp', 'show')}
            hideTooltip={getPasswordVisibilityLabel('smtp', 'hide')}
          />
          {hasError('smtpPass') && (
            <FormHelperText>
              {vtr(getError('smtpPass'), {
                field: t('email_configuration.form.password'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <Box mt={'20px'}>
          <FormControlLabel
            name="smtpSecure"
            checked={values.smtpSecure}
            onChange={handleChange}
            control={<Checkbox color="primary" />}
            label={t('email_configuration.form.secure')}
            labelPlacement="end"
            disabled={isFetching || !values.useSmtp}
          />
        </Box>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('smtpFromEmail')}
          variant="outlined"
        >
          <InputLabel htmlFor="smtpFromEmail">{`${t('email_configuration.form.sender_email')} ${t('email_configuration.form.sender_email_hint')}`}</InputLabel>
          <OutlinedInput
            name="smtpFromEmail"
            label={`${t('email_configuration.form.sender_email')} ${t('email_configuration.form.sender_email_hint')}`}
            value={values.smtpFromEmail}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
            data-cy="create-email-configuration-form-sender-email-input"
          />
          {hasError('smtpFromEmail') && (
            <FormHelperText data-cy="create-email-configuration-form-sender-email-error">
              {vtr(getError('smtpFromEmail'), {
                field: t('email_configuration.form.sender_email'),
                field2: t('email_configuration.form.username'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </>
    ),
    [
      formClasses.formControl,
      formClasses.widthFull,
      getError,
      getPasswordVisibilityLabel,
      handleBlur,
      handleChange,
      hasError,
      isFetching,
      t,
      values.smtpFromEmail,
      values.smtpHost,
      values.smtpPass,
      values.smtpPort,
      values.smtpSecure,
      values.smtpUser,
      values.useSmtp,
      vtr,
    ],
  );

  const gmailForm = useMemo(
    () => (
      <>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('emailAddress')}
          variant="outlined"
        >
          <InputLabel htmlFor="emailAddress">{t('email_configuration.form.email_address')}</InputLabel>
          <OutlinedInput
            name="emailAddress"
            label={t('email_configuration.form.email_address')}
            type="email"
            value={values.emailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isFetching}
          />
          {hasError('emailAddress') && (
            <FormHelperText>
              {vtr(getError('emailAddress'), {
                field: t('email_configuration.form.email_address'),
              })}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          error={hasError('emailToken')}
          variant="outlined"
        >
          <InputLabel htmlFor="emailToken">{t('email_configuration.form.email_token')}</InputLabel>
          <Box display="flex" flexDirection="row" alignItems="center" gap={16}>
            <OutlinedPasswordInput
              name="emailToken"
              label={t('email_configuration.form.email_token')}
              value={values.emailToken}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isFetching}
              fullWidth
              showTooltip={getPasswordVisibilityLabel('gmail', 'show')}
              hideTooltip={getPasswordVisibilityLabel('gmail', 'hide')}
            />
            <Tooltip title={t('email_configuration.token.tooltip')}>
              <IconButton href={googleHelpUrl} target="_blank" size="large">
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {hasError('emailToken') && (
            <FormHelperText>
              {vtr(getError('emailToken'), {
                field: t('email_configuration.form.email_token'),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </>
    ),
    [
      formClasses.formControl,
      formClasses.widthFull,
      getError,
      getPasswordVisibilityLabel,
      googleHelpUrl,
      handleBlur,
      handleChange,
      hasError,
      isFetching,
      t,
      values.emailAddress,
      values.emailToken,
      vtr,
    ],
  );

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <FormControl className={cx(formClasses.formControl, formClasses.widthFull)} variant="outlined">
        <HUAutocomplete
          renderInput={renderAutocompleteInput}
          options={Object.values(EmailType)}
          onChange={handleChangeForm}
          value={values.type}
          disableClearable
          disabled={isFetching}
        />
      </FormControl>
      {values.type === EmailType.SMTP ? smtpForm : gmailForm}
      <div className={classes.formActions}>
        <Button fullWidth size="small" variant="outlined" disabled={isFetching} onClick={onSkip}>
          {t(`${Namespaces.Setup}:page.buttons.skip`)}
        </Button>
        <Button
          fullWidth
          size="small"
          variant="contained"
          type="submit"
          disabled={isFetching}
          data-cy="setup-email-configuration-form-submit"
        >
          {t(`${Namespaces.Setup}:page.buttons.submit`)}
        </Button>
      </div>
    </form>
  );
});
